/**
 * Analytics Tracker
 *
 * Batches events, persists to AsyncStorage, deduplicates, and flushes to Supabase.
 * Silent on errors — never throws.
 *
 * CONSENT GATE. Usage analytics is first-party but NOT essential, so the user
 * can switch it off (Settings → Your Data → Usage analytics, or Manage my data).
 * The choice is ONE fact, stored on the ACCOUNT: `profiles.analytics_opt_out`
 * (Phase 118-08), which the database also enforces (a BEFORE INSERT trigger
 * drops an opted-out user's analytics_events rows). This gate is the client
 * half of that same fact, so nothing is even queued on the device:
 *
 *   - consent is held FOR A USER (`setConsent(userId, granted)`), fed by the
 *     AuthProvider from the signed-in user's profile row;
 *   - until the current user's flag has been read, the gate is `pending`:
 *     events are held IN MEMORY ONLY — never persisted, never sent — and
 *     flush() is a no-op;
 *   - `granted` releases them; `denied` drops them and clears the persisted queue;
 *   - sign-out (`reset()`) and a different identified user both put the gate
 *     back to `pending`, so one person's choice never applies to the next.
 *
 * "Still loading" is never treated as "allowed" — the inverse of the
 * entitlement loading gate, because here a false positive is the harm.
 */

import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { supabase } from '@/lib/supabase/client';
import type { Database } from '@/lib/supabase/database.types';
import { mode } from '@/lib/config/app-mode';
import { getDeviceInfo, getAppVersion } from '@/lib/feedback/device-info';
import { reportError } from '@/lib/errors';
import { EVENT_CATEGORIES } from './types';
import type { AnalyticsEventName, QueuedEvent } from './types';

const STORAGE_KEY = '@inverio/analytics_queue';
const FLUSH_INTERVAL_MS = 10_000; // 10 seconds
const BATCH_SIZE = 20;
const MAX_QUEUE_SIZE = 200;
const DEDUP_WINDOW_MS = 500;

/** `pending` until the current user's account flag has been read (see file header). */
export type AnalyticsConsent = 'pending' | 'granted' | 'denied';
type ConsentListener = (consent: AnalyticsConsent) => void;

class AnalyticsTracker {
  private queue: QueuedEvent[] = [];
  private userId: string | null = null;
  private sessionId: string | null = null;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private dedupCache = new Map<string, number>();
  private initialized = false;
  /** The last account flag applied, and whose it is. */
  private consentDecision: { userId: string; granted: boolean } | null = null;
  private consentListeners = new Set<ConsentListener>();

  /** Restore persisted queue and start flush timer */
  async init(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;

    try {
      const stored = await AsyncStorage.getItem(STORAGE_KEY);
      // An opt-out can land while this read is in flight; a queue restored
      // after it would resurrect events the user has just refused.
      if (stored && this.getConsent() !== 'denied') {
        const parsed = JSON.parse(stored) as QueuedEvent[];
        this.queue = [...parsed, ...this.queue].slice(0, MAX_QUEUE_SIZE);
      }
    } catch (e) {
      // Non-fatal — a corrupt/absent queue just starts empty. Report for diagnostics only.
      reportError(e, { context: 'AnalyticsTracker.initRestore', level: 'warning', silent: true });
    }

    this.flushTimer = setInterval(() => {
      this.flush().catch((err) => reportError(err, { context: 'AnalyticsTracker.flushInterval', silent: true }));
    }, FLUSH_INTERVAL_MS);
  }

  /**
   * Apply a user's usage-analytics choice (`!profiles.analytics_opt_out`).
   * Called by the AuthProvider whenever the signed-in user's profile is read,
   * and by the opt-out mutation the moment the switch is turned off. It only
   * opens the gate while `userId` is the identified user.
   * Denying drops everything queued, in memory and on the device.
   */
  setConsent(userId: string, granted: boolean): void {
    this.consentDecision = { userId, granted };
    this.applyConsent();
  }

  /** React to a change of the effective consent (decision or identity). */
  private applyConsent(): void {
    const next = this.getConsent();
    if (next === 'denied') {
      this.queue = [];
      this.dedupCache.clear();
      AsyncStorage.removeItem(STORAGE_KEY).catch((err) =>
        reportError(err, { context: 'AnalyticsTracker.setConsent.clearQueue', level: 'warning' }),
      );
    } else if (this.queue.length > 0) {
      // Events held in memory while pending may now be kept across restarts.
      this.persistQueue();
    }
    this.consentListeners.forEach((listener) => listener(next));
  }

  /** Current consent state — `pending` until the identified user's flag is read. */
  getConsent(): AnalyticsConsent {
    const d = this.consentDecision;
    if (!d || !this.userId || d.userId !== this.userId) return 'pending';
    return d.granted ? 'granted' : 'denied';
  }

  /** True only once the user's choice is known AND allows analytics. */
  isEnabled(): boolean {
    return this.getConsent() === 'granted';
  }

  /** Observe consent changes (the session manager records a session on grant). */
  onConsentChange(listener: ConsentListener): () => void {
    this.consentListeners.add(listener);
    return () => {
      this.consentListeners.delete(listener);
    };
  }

  /** Set current user ID */
  identify(userId: string): void {
    if (this.userId === userId) return;
    const before = this.getConsent();
    this.userId = userId;
    // Identity changed: the effective consent may have changed with it (a
    // decision recorded for this user just before identify(), or none yet).
    if (this.getConsent() !== before) this.applyConsent();
  }

  /** Set current session ID */
  setSessionId(sessionId: string | null): void {
    this.sessionId = sessionId;
  }

  /**
   * Clear identity + all queued events. Call on sign-out so the departed user's
   * events aren't flushed by the now-anonymous client (rejected by the
   * analytics_events RLS policy, Postgres 42501) and don't bleed into the next
   * user on the same device.
   *
   * Consent is reset with the identity: it belongs to the account that signed
   * out, and the next person's choice is read from THEIR profile.
   */
  reset(): void {
    this.userId = null;
    this.consentDecision = null;
    this.sessionId = null;
    this.queue = [];
    this.dedupCache.clear();
    AsyncStorage.removeItem(STORAGE_KEY).catch(() => { /* best-effort local clear */ });
  }

  /** Track an analytics event */
  track(eventName: AnalyticsEventName, properties: Record<string, unknown> = {}): void {
    if (!this.userId || this.getConsent() === 'denied') return;

    // Dedup: skip same event+properties within 500ms
    const dedupKey = `${eventName}:${JSON.stringify(properties)}`;
    const now = Date.now();
    const lastSeen = this.dedupCache.get(dedupKey);
    if (lastSeen && now - lastSeen < DEDUP_WINDOW_MS) return;
    this.dedupCache.set(dedupKey, now);

    // Clean old dedup entries periodically
    if (this.dedupCache.size > 100) {
      const cutoff = now - DEDUP_WINDOW_MS;
      for (const [key, ts] of this.dedupCache) {
        if (ts < cutoff) this.dedupCache.delete(key);
      }
    }

    const event: QueuedEvent = {
      user_id: this.userId,
      session_id: this.sessionId,
      event_name: eventName,
      event_category: EVENT_CATEGORIES[eventName] ?? 'interaction',
      properties,
      platform: Platform.OS,
      app_version: getAppVersion(),
      device_info: getDeviceInfo() as unknown as Record<string, unknown>,
      created_at: new Date().toISOString(),
    };

    if (mode.isMock) {
      console.debug('[Analytics]', eventName, properties);
      return;
    }

    this.queue.push(event);
    if (this.queue.length > MAX_QUEUE_SIZE) this.queue.splice(0, this.queue.length - MAX_QUEUE_SIZE);

    // The user's choice is not known yet: hold in memory only (see file header).
    if (!this.isEnabled()) return;

    // Auto-flush when batch size reached
    if (this.queue.length >= BATCH_SIZE) {
      this.flush().catch((err) => reportError(err, { context: 'AnalyticsTracker.autoFlush', silent: true }));
    }

    // Persist queue (fire-and-forget)
    this.persistQueue();
  }

  /** Flush queued events to Supabase */
  async flush(): Promise<void> {
    if (mode.isMock || !this.isEnabled() || this.queue.length === 0) return;

    const batch = this.queue.splice(0, BATCH_SIZE);

    try {
      const { error } = await supabase
        .from('analytics_events')
        .insert(batch as unknown as Database['public']['Tables']['analytics_events']['Insert'][]);

      if (error) {
        // Server REJECTED the batch (RLS / auth / validation). Retrying the same
        // payload will fail identically — DROP it. Re-queueing a permanent failure
        // (e.g. RLS 42501 after sign-out) loops forever and spams errors. Only
        // transient transport failures (thrown, handled in catch) are re-queued.
        //
        // These are EXPECTED whenever the client is unauthenticated or the
        // referenced user is gone — not errors worth surfacing (reportError →
        // console.error trips the dev error overlay). Drop them SILENTLY; any
        // OTHER rejection is still reported. (Adi 2026-07-22.)
        //   42501 — RLS reject (after sign-out / pre-email-confirmation window;
        //           the INSERT policy requires auth.uid() = user_id).
        //   23503 — FK violation on user_id (the user was deleted, or queued
        //           events predate the current session / reference a gone user).
        const code = (error as { code?: string }).code;
        if (code !== '42501' && code !== '23503') {
          reportError(error, { context: 'AnalyticsTracker.flush', silent: true });
        }
      }
    } catch (err) {
      // Transport/network failure (request never reached the server) — re-queue
      // for retry; this is transient, unlike a server rejection above. Not if
      // the user opted out while the request was in flight.
      if (this.isEnabled()) this.queue.unshift(...batch);
      reportError(err, { context: 'AnalyticsTracker.flush.network', silent: true });
    }

    // Persist remaining queue
    this.persistQueue();
  }

  /** Stop the flush timer */
  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    this.initialized = false;
  }

  /** Persist queue to AsyncStorage (capped at MAX_QUEUE_SIZE) */
  private persistQueue(): void {
    if (!this.isEnabled()) return;
    const toStore = this.queue.slice(0, MAX_QUEUE_SIZE);
    AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(toStore)).catch((err) => reportError(err, { context: 'AnalyticsTracker.persistQueue', silent: true }));
  }
}

/** Singleton tracker instance */
export const analyticsTracker = new AnalyticsTracker();
